/**
 * Returns a new array with the elements randomly reordered, using the
 * Fisher–Yates shuffle (unbiased: each permutation is equally likely). The
 * input array is not mutated. `random` is injectable for deterministic tests.
 */
export function shuffleArray<T>(
    items: T[],
    random: () => number = Math.random
): T[] {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(random() * (i + 1));
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}

/** A random permutation of [0, length). */
export function buildFullShuffleOrder(
    length: number,
    random: () => number = Math.random
): number[] {
    const indices = Array.from({ length }, (_, i) => i);
    return shuffleArray(indices, random);
}

/**
 * Build a playback order for shuffle-enable that places `currentIndex` at the
 * FRONT of the order and randomises every other index after it.
 *
 * EVE-13081: enabling shuffle puts the current track at the start of the shuffle
 * queue (Android parity), so "next" can walk every remaining track (N-1 steps on
 * an N-track playlist — even when shuffle is enabled while on the last track) and
 * "previous" is a no-op at the moment of enable (you are at the start of the
 * queue). The current track keeps playing uninterrupted because it stays first.
 *
 * Supersedes EVE-11931's "keep the current track at its own position", which left
 * "next" dead when shuffle was enabled on the last track.
 */
export function buildShuffleOrder(
    length: number,
    currentIndex: number,
    random: () => number = Math.random
): number[] {
    if (length <= 1) return Array.from({ length }, (_, i) => i);
    const others: number[] = [];
    for (let i = 0; i < length; i++) {
        if (i !== currentIndex) others.push(i);
    }
    return [currentIndex, ...shuffleArray(others, random)];
}
